import { account, ID } from "./appwrite";
import type { Models } from "appwrite";

export type User = Models.User<Models.Preferences>;

/**
 * Sign up a new user with email and password
 */
export async function signUp(
  email: string,
  password: string,
  name: string
): Promise<User> {
  try {
    return await account.create(ID.unique(), email, password, name);
  } catch (error: any) {
    console.error("SignUp Error — code:", error.code, "type:", error.type, "message:", error.message);
    if (error.code === 409) {
      throw new Error("An account with this email already exists");
    }
    if (error.type === "user_password_policy_violation") {
      throw new Error("Password doesn't meet requirements. Try a stronger password (mix of letters, numbers, and symbols).");
    }
    if (error.code === 400) {
      throw new Error(`Sign up failed: ${error.message}`);
    }
    throw new Error(
      error.message || "Failed to create account. Please try again."
    );
  }
}

/**
 * Sign in with email and password
 */
export async function signIn(
  email: string,
  password: string
): Promise<Models.Session> {
  try {
    return await account.createEmailPasswordSession(email, password);
  } catch (error: any) {
    // Log the full error so we can see exactly what Appwrite returns
    console.error("SignIn Error — code:", error.code, "type:", error.type, "message:", error.message);

    const type: string = error.type ?? "";
    const code: number = error.code ?? 0;

    // Email verification required
    if (type === "user_email_not_whitelisted" || type === "user_invalid_token") {
      throw new Error("Please verify your email before signing in. Check your inbox for a verification link.");
    }
    // Blocked account
    if (type === "user_blocked") {
      throw new Error("Your account has been blocked. Please contact support.");
    }
    // Session already exists
    if (type === "user_session_already_exists") {
      // Already logged in — just return the current session by refreshing
      return account.getSession("current") as any;
    }
    // Wrong credentials
    if (code === 401) {
      throw new Error("Incorrect email or password. Please try again.");
    }
    if (code === 429) {
      throw new Error("Too many login attempts. Please wait a moment and try again.");
    }
    // Fallback: show the raw Appwrite message so nothing is hidden
    throw new Error(error.message || "Sign in failed. Please try again.");
  }
}

/**
 * Sign out current user
 */
export async function signOut(): Promise<void> {
  await account.deleteSession("current");
}

/**
 * Get current logged in user
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    return await account.get();
  } catch {
    return null;
  }
}

/**
 * Send email verification
 */
export async function sendVerificationEmail(
  redirectUrl: string
): Promise<Models.Token> {
  return await account.createVerification(redirectUrl);
}

/**
 * Complete email verification
 */
export async function verifyEmail(
  userId: string,
  secret: string
): Promise<Models.Token> {
  return await account.updateVerification(userId, secret);
}

/**
 * Check if user's email is verified
 */
export async function isEmailVerified(): Promise<boolean> {
  try {
    const user = await account.get();
    return user.emailVerification;
  } catch {
    return false;
  }
}

/**
 * Send magic URL for passwordless login
 */
export async function sendMagicLink(
  email: string,
  redirectUrl: string
): Promise<Models.Token> {
  return await account.createMagicURLToken(ID.unique(), email, redirectUrl);
}

/**
 * Complete magic URL session
 */
export async function completeMagicLink(
  userId: string,
  secret: string
): Promise<Models.Session> {
  return await account.createSession(userId, secret);
}

/**
 * Send a password-reset email.
 * Appwrite emails the user a link to /reset-password?userId=...&secret=...
 */
export async function sendPasswordReset(email: string): Promise<void> {
  const origin = window.location.origin;
  await account.createRecovery(email, `${origin}/reset-password`);
}

/**
 * Complete the password reset using the token from the email link.
 */
export async function completePasswordReset(
  userId: string,
  secret: string,
  newPassword: string
): Promise<void> {
  await account.updateRecovery(userId, secret, newPassword);
}
